// Imperative wrapper around alphaTab's AlphaTabApi. Owns the single API instance; React mounts it
// via attach()/detach(). Fonts + soundfont are self-hosted (bundled) at /alphatab/* for PWA offline.
// alphaTab is loaded as a classic <script> from /alphatab/alphaTab.min.js (see index.html), NOT
// bundled — so alphaTab detects its own location (document.currentScript) and resolves its
// worker/worklet/font/soundfont under /alphatab/. Vite can't rewrite alphaTab's internal worker URL,
// so this script-tag approach is the reliable self-hosted path. Types are import-type-only (erased).
import type * as AlphaTabNS from "@coderline/alphatab";
import { rawToBeatIndex } from "../core/notation/beatGrouping";
import type { NoteEvent } from "../types/domain";
const alphaTab = (window as unknown as { alphaTab: typeof AlphaTabNS }).alphaTab;

export interface StyleOpts {
  editMode: boolean;
  activeMeasure: number;
  selectedNoteIdx: number;
  insertArmed: boolean;
  notesByMeasure: Record<number, NoteEvent[]>;   // arrangement notes per measure (for selection + modified tint)
  insertedMeasures?: Set<number>;
  incomplete?: Set<number>;                       // measures that don't fill the bar → warning tint
  rangeByMeasure?: Record<number, Set<number>>;    // 범위 selection: beat indices per measure to highlight (can span measures)
}

const FONT_DIR = "/alphatab/font/";
const SOUND_FONT = "/alphatab/soundfont/sonivox.sf2";

export type BeatClickHandler = (info: { measureIndex: number; beatInBar: number; absolutePlaybackStart: number }) => void;

export class ScoreController {
  private api: any = null;
  private host: HTMLElement | null = null;
  private ro: ResizeObserver | null = null;
  private lastLayoutKey = "";
  ready = false;
  playing = false;

  onScoreLoaded: (() => void) | null = null;
  onBeatClick: BeatClickHandler | null = null;
  onRendered: (() => void) | null = null;
  onPlayerStateChanged: ((playing: boolean) => void) | null = null;
  styleProvider: (() => StyleOpts | null) | null = null;   // editor supplies selection/modified info

  attach(host: HTMLElement, masterVolume = 1): void {
    if (this.api && this.host === host) return; // idempotent (StrictMode double-mount safe)
    if (this.api) this.detach();
    this.host = host;
    host.innerHTML = "";
    this.api = new alphaTab.AlphaTabApi(host, {
      // run layout + synth on the main thread: a bundler doesn't emit alphaTab.worker.mjs at a
      // resolvable URL, so workers 404. Main-thread is fine for a chord chart and avoids the issue.
      core: { fontDirectory: FONT_DIR },
      display: { scale: 1.15, layoutMode: alphaTab.LayoutMode.Page, barsPerRow: 4, padding: [6, 10, 6, 6] },
      player: {
        enablePlayer: true,
        soundFont: SOUND_FONT,
        enableCursor: true,
        // alphaTab's built-in click-to-seek + drag selection throws over empty space and we do our
        // own click/seek/range handling — turn it off so those internal handlers don't run.
        enableUserInteraction: false,
        scrollMode: alphaTab.ScrollMode.Off,
      },
    });
    this.ready = false;
    this.api.scoreLoaded.on(() => {
      this.ready = true; this.applyResponsiveLayout(true);
      this.applyStyles();   // re-apply selection/modified coloring after every (re)render
      this.onScoreLoaded?.();
    });
    // re-fit scale + bars-per-row to the container as it resizes (mobile ↔ desktop)
    this.ro = new ResizeObserver(() => this.applyResponsiveLayout(false));
    this.ro.observe(host);
    this.api.beatMouseDown.on((beat: any) => {
      this.onBeatClick?.({
        measureIndex: beat.voice.bar.index,
        beatInBar: beat.voice.beats.indexOf(beat),
        absolutePlaybackStart: beat.absolutePlaybackStart,
      });
    });
    // NOTE: we intentionally do NOT subscribe to beatMouseMove/beatMouseUp — alphaTab 1.8.x throws
    // internally when those fire over empty space. Drag-select is driven from our own pointer
    // listeners in App via beatAtClient() (the boundsLookup hit-test), which never throws.
    this.api.renderFinished.on(() => { this.onRendered?.(); });
    this.api.playerStateChanged.on((e: any) => {
      this.playing = e.state === alphaTab.synth.PlayerState.Playing;
      this.onPlayerStateChanged?.(this.playing);
    });
    this.api.masterVolume = masterVolume;
  }

  detach(): void {
    try { this.ro?.disconnect(); } catch { /* noop */ }
    this.ro = null;
    try { this.api?.destroy(); } catch { /* noop */ }
    this.api = null;
    this.host = null;
    this.ready = false;
    this.lastLayoutKey = "";
  }

  // pick scale + bars-per-row from the available width. Each bar needs ~320px+ to render legibly;
  // dense bars (16th runs, grace-note licks) overlap/tangle when squeezed below that — so phones get
  // ONE bar per row (full width) rather than cramming two. Only re-renders when the bucket changes.
  private layoutFor(w: number): { barsPerRow: number; scale: number } {
    if (w < 360) return { barsPerRow: 1, scale: 0.9 };
    if (w < 640) return { barsPerRow: 1, scale: 1.0 };    // phones: 1 bar/row, full width (was 2 → tangled)
    if (w < 980) return { barsPerRow: 2, scale: 1.0 };    // ≥320px per bar
    if (w < 1320) return { barsPerRow: 3, scale: 1.1 };
    return { barsPerRow: 4, scale: 1.15 };
  }

  private applyResponsiveLayout(force: boolean): void {
    if (!this.api || !this.host) return;
    const w = this.host.clientWidth || this.host.parentElement?.clientWidth || 0;
    if (!w) return;
    const { barsPerRow, scale } = this.layoutFor(w);
    const key = `${barsPerRow}:${scale}`;
    if (!force && key === this.lastLayoutKey) return;
    this.lastLayoutKey = key;
    try {
      this.api.settings.display.scale = scale;
      this.api.settings.display.barsPerRow = barsPerRow;
      this.api.updateSettings();
      this.api.render();
    } catch { /* not ready */ }
  }

  setTex(tex: string): void {
    if (!this.api) return;
    this.api.tex(tex);
  }

  // force a re-layout/re-render — needed when the score becomes visible after being mounted hidden
  // (display:none has 0 width, so the responsive layout skipped it until now).
  relayout(): void {
    this.applyResponsiveLayout(true);
  }

  get score(): any { return this.api?.score ?? null; }
  get rawApi(): any { return this.api; }

  setTickPosition(tick: number): void {
    if (!this.api) return;
    try { this.api.tickPosition = tick; } catch { /* not ready */ }
  }

  // hide the alphaTab playback cursor (bar tint + beat line) when not playing
  setCursorVisible(visible: boolean): void {
    if (this.host) this.host.classList.toggle("cursor-hidden", !visible);
  }

  // re-apply selection/modified-measure coloring to the live score model, then re-render. Used for
  // selection-only changes (no tex rebuild). Ported from poc chartStyleScore.
  restyle(): void { this.applyStyles(); try { this.api?.render(); } catch { /* noop */ } }

  private applyStyles(): void {
    const m = window as any;
    if (!this.api || !this.api.score || !this.styleProvider) return;
    const opts = this.styleProvider();
    if (!opts) return;
    const bars = this.api.score.tracks[0].staves[0].bars;
    const Color = alphaTab.model.Color;
    const NoteStyle = alphaTab.model.NoteStyle, BeatStyle = alphaTab.model.BeatStyle;
    const NSub = alphaTab.model.NoteSubElement, BSub = alphaTab.model.BeatSubElement;
    const colorNote = (note: any, c: any) => {
      note.style = new NoteStyle();
      note.style.colors.set(NSub.GuitarTabFretNumber, c);
      note.style.colors.set(NSub.StandardNotationNoteHead, c);
    };
    const colorRest = (bt: any, c: any) => {
      bt.style = new BeatStyle();
      bt.style.colors.set(BSub.GuitarTabRests, c);
      bt.style.colors.set(BSub.StandardNotationRests, c);
    };
    const tintBar = (bar: any, c: any) => bar.voices[0].beats.forEach((bt: any) => {
      if (bt.notes.length) bt.notes.forEach((n: any) => colorNote(n, c)); else colorRest(bt, c);
    });
    // reset
    bars.forEach((b: any) => b.voices[0].beats.forEach((bt: any) => { bt.notes.forEach((n: any) => { n.style = null; }); bt.style = null; }));
    // modified measures (inserted, or has any real note) → blue tint
    const insColor = new Color(86, 156, 255, 255);
    const isMod = (i: number) => (opts.insertedMeasures?.has(i))
      || (opts.notesByMeasure[i] && opts.notesByMeasure[i].some((n) => !n.rest));
    bars.forEach((bar: any, i: number) => { if (isMod(i)) tintBar(bar, insColor); });
    // under-filled measures → red warning tint (overrides the blue modified tint)
    const warnColor = new Color(255, 90, 90, 255);
    bars.forEach((bar: any, i: number) => { if (opts.incomplete?.has(i)) tintBar(bar, warnColor); });
    void m;
    if (!opts.editMode) return;
    // 범위 selection highlight (cyan) — spans measures, independent of activeMeasure
    if (opts.rangeByMeasure) {
      const rangeCol = new Color(34, 211, 238, 255);
      for (const k in opts.rangeByMeasure) {
        const rb = bars[+k]; if (!rb) continue;
        const rbeats = rb.voices[0].beats;
        opts.rangeByMeasure[+k].forEach((bi) => {
          const bt = rbeats[bi];
          if (bt) { if (bt.notes.length) bt.notes.forEach((n: any) => colorNote(n, rangeCol)); else colorRest(bt, rangeCol); }
        });
      }
      return;
    }
    if (opts.activeMeasure < 0) return;
    const bar = bars[opts.activeMeasure];
    if (!bar) return;
    const beats = bar.voices[0].beats;
    const sel = opts.insertArmed ? new Color(64, 224, 132, 255) : new Color(255, 214, 0, 255);
    const notes = opts.notesByMeasure[opts.activeMeasure] || [];
    if (opts.selectedNoteIdx >= 0) {
      const bi = rawToBeatIndex(notes, opts.selectedNoteIdx);
      const bt = beats[bi];
      if (bt) { if (bt.notes.length) bt.notes.forEach((n: any) => colorNote(n, sel)); else colorRest(bt, sel); }
    }
  }

  setMasterVolume(v: number): void {
    if (this.api) this.api.masterVolume = v;
  }

  // ---- geometry for the 범위 selection handles ----
  hostEl(): HTMLElement | null { return this.host; }
  private beatObj(measureIndex: number, beatInBar: number): any {
    try { return this.api?.score?.tracks?.[0]?.staves?.[0]?.bars?.[measureIndex]?.voices?.[0]?.beats?.[beatInBar] ?? null; }
    catch { return null; }
  }
  // visual bounds (surface px) of a beat, or null
  beatBounds(measureIndex: number, beatInBar: number): { x: number; y: number; w: number; h: number } | null {
    try {
      const bl = this.api?.renderer?.boundsLookup;
      const beat = this.beatObj(measureIndex, beatInBar);
      if (!bl || !beat) return null;
      const bb = bl.findBeat(beat);
      const vb = bb?.visualBounds;
      return vb ? { x: vb.x, y: vb.y, w: vb.w, h: vb.h } : null;
    } catch { return null; }
  }
  // beat under a client point (for handle drag hit-testing), or null
  beatAtClient(clientX: number, clientY: number): { measureIndex: number; beatInBar: number } | null {
    try {
      const bl = this.api?.renderer?.boundsLookup;
      if (!bl || !this.host) return null;
      const r = this.host.getBoundingClientRect();
      const bt = bl.getBeatAtPos(clientX - r.left, clientY - r.top);
      if (!bt) return null;
      return { measureIndex: bt.voice.bar.index, beatInBar: bt.voice.beats.indexOf(bt) };
    } catch { return null; }
  }

  // score-mode playback (alphaTab's own synth) — wired in Step 4
  play(): void { try { this.api?.play(); } catch { /* noop */ } }
  pause(): void { try { this.api?.pause(); } catch { /* noop */ } }
  stop(): void { try { this.api?.stop(); } catch { /* noop */ } }

  // resume an auto-suspended WebAudio context (backgrounded tab / autoplay policy)
  resumeAudio(): void {
    try {
      const ctx = this.api?.player?.output?.context;
      if (ctx && ctx.state !== "running" && ctx.resume) ctx.resume();
    } catch { /* noop */ }
  }
}
